import { Constants } from "@core/Utils/Constants";
import { ButtonState } from "../Button";

export class MenuButton extends Phaser.GameObjects.Container {

    public buttonFunction: () => void;
    public padding: number = 0;

    protected background: Phaser.GameObjects.Graphics;
    protected buttonText: Phaser.GameObjects.Text;
    protected buttonIcon: Phaser.GameObjects.Image;

    private previousState;

    constructor(scene: Phaser.Scene, text: string, spriteSheet: string, icon: string) {
        super(scene, 0, 0);

        this.state = ButtonState.Enabled;

        this.background = new Phaser.GameObjects.Graphics(this.scene);
        this.background.setInteractive(new Phaser.Geom.Rectangle(0, 0, this.width, this.height), Phaser.Geom.Rectangle.Contains)
            .on(Phaser.Input.Events.GAMEOBJECT_POINTER_UP, this.handleUp, this)
            .on(Phaser.Input.Events.GAMEOBJECT_POINTER_OUT, this.handleOut, this)
            .on(Phaser.Input.Events.GAMEOBJECT_POINTER_DOWN, this.handleDown, this)
            .on(Phaser.Input.Events.GAMEOBJECT_POINTER_OVER, this.handleOver, this)
        this.add(this.background);

        this.buttonIcon = new Phaser.GameObjects.Image(scene, 0, 0, spriteSheet, icon);
        this.buttonIcon.setOrigin(0.5);
        this.add(this.buttonIcon);

        this.buttonText = new Phaser.GameObjects.Text(scene, 0, 0, text, Constants.MENU_BUTTON_STYLE_FONT);
        this.buttonText.setOrigin(0, 0.5);
        this.add(this.buttonText);
    }

    public ResizeAndMove(x: number, y: number, width: number, singleButtonHeight: number, padding: number) {
        this.x = x;
        this.y = y;
        this.width = width;
        this.height = singleButtonHeight;

        this.padding = padding;

        this.background.clear();

        if (this.state == ButtonState.Disabled) {
            this.buttonText.setStyle(Constants.MENU_BUTTON_STYLE_FONT_DISABLED);
            this.background.fillGradientStyle(0x000000, 0x000000, 0x000000, 0x000000, 0.5, 0, 0.5, 0);
            this.buttonIcon.setTint(0x8a8a8a);
        }
        else {
            this.buttonText.setStyle(Constants.MENU_BUTTON_STYLE_FONT);
            this.background.fillGradientStyle(0x000000, 0x000000, 0x000000, 0x000000, 0.3, 0, 0.3, 0);
            this.buttonIcon.clearTint();
        }

        this.background.fillRect(0, 0, width, this.height);
        this.background.input.hitArea.setSize(width, this.height);

        //desktop has smaller texts and icons
        if (this.scene.game.device.os.desktop) {
            this.buttonText.setFontSize(singleButtonHeight / 2.5);
            this.buttonIcon.setDisplaySize(singleButtonHeight / 2, singleButtonHeight / 2);
        }
        else {
            this.buttonText.setFontSize(singleButtonHeight / 1.85);
            this.buttonIcon.setDisplaySize(singleButtonHeight / 1.65, singleButtonHeight / 1.65);
        }

        this.buttonIcon.setPosition((this.buttonIcon.displayWidth / 2) + (this.padding * 2), singleButtonHeight / 2);
        this.buttonText.setPosition(this.buttonIcon.x + this.buttonIcon.displayWidth / 2 + (this.padding * 5), singleButtonHeight / 2);
    }

    public setDisabled(disabled: boolean): void {
        if (disabled) {
            this.state = ButtonState.Disabled;
        }
        else {
            this.state = ButtonState.Enabled;
        }

        if (this.previousState != this.state) {
            this.background.clear();
            if (this.state == ButtonState.Disabled) {
                this.buttonText.setStyle(Constants.MENU_BUTTON_STYLE_FONT_DISABLED);
                this.background.fillGradientStyle(0x000000, 0x000000, 0x000000, 0x000000, 0.5, 0, 0.5, 0);
                this.buttonIcon.setTint(0x8a8a8a);
            }
            else {
                this.buttonText.setStyle(Constants.MENU_BUTTON_STYLE_FONT);
                this.background.fillGradientStyle(0x000000, 0x000000, 0x000000, 0x000000, 0.3, 0, 0.3, 0);
                this.buttonIcon.clearTint();
            }

            //desktop has smaller texts and icons
            if (this.scene.game.device.os.desktop) {
                this.buttonText.setFontSize(this.height / 2.5);
                this.buttonIcon.setDisplaySize(this.height / 2, this.height / 2);
            }
            else {
                this.buttonText.setFontSize(this.height / 1.85);
                this.buttonIcon.setDisplaySize(this.height / 1.65, this.height / 1.65);
            }

            this.background.fillRect(0, 0, this.width, this.height);
            this.background.input.hitArea.setSize(this.width, this.height);
            this.previousState = this.state;
        }
    }

    protected handleUp(m, x, y, e): void {
        if (this.state == ButtonState.Disabled) {
            e.stopPropagation();
            return;
        }
        this.state = ButtonState.Enabled;

        if (this.buttonFunction)
            this.buttonFunction();

        this.background.clear();
        this.background.fillGradientStyle(0x000000, 0x000000, 0x000000, 0x000000, 0.3, 0, 0.3, 0);
        this.background.fillRect(0, 0, this.width, this.height);
    }

    protected handleOut(m, x, y, e): void {
        if (this.state == ButtonState.Disabled)
            return;

        this.state = ButtonState.Enabled;

        this.background.clear();
        this.background.fillGradientStyle(0x000000, 0x000000, 0x000000, 0x000000, 0.3, 0, 0.3, 0);
        this.background.fillRect(0, 0, this.width, this.height);
    }

    protected handleDown(m, x, y, e): void {
        if (this.state == ButtonState.Disabled)
            return;

        this.state = ButtonState.Down;

        this.background.clear();
        this.background.fillGradientStyle(0xffffff, 0xffffff, 0xffffff, 0xffffff, 0.4, 0, 0.4, 0);
        this.background.fillRect(0, 0, this.width, this.height);
    }

    protected handleOver(m, x, y, e): void {
        e.stopPropagation();

        if (this.state == ButtonState.Disabled)
            return;

        this.state = ButtonState.Hover;

        this.background.clear();
        this.background.fillGradientStyle(0x909090, 0x909090, 0x909090, 0x909090, 0.4, 0, 0.4, 0);
        this.background.fillRect(0, 0, this.width, this.height);
    }
}